'use client'

import { Box, Typography } from '@mui/material'
import { Icon, Image, ImageData, IconName } from '@media-components'
import { getStyles } from '@utils/styles'
import defaultStyles from './styles'

export type FollowUsSocialLinkData = {
  label: string
  link: string
  imageData?: ImageData
}

export type FollowUsOnSocialMediaData = {
  title?: string
  links: FollowUsSocialLinkData[]
}

type SocialPlatformConfig = {
  iconName: IconName
  color: string
}

const SOCIAL_PLATFORM_CONFIG: Record<string, SocialPlatformConfig> = {
  facebook: { iconName: 'Facebook', color: '#1877F2' },
  twitter: { iconName: 'Twitter', color: '#000000' },
  x: { iconName: 'Twitter', color: '#000000' },
  instagram: { iconName: 'Instagram', color: '#E4405F' },
  linkedin: { iconName: 'LinkedIn', color: '#0A66C2' },
  youtube: { iconName: 'YoutubeReview', color: '#FF0000' },
}

const getPlatformConfig = (label: string): SocialPlatformConfig | undefined => {
  const key = label.trim().toLowerCase().replace(/\s+/g, '')
  return SOCIAL_PLATFORM_CONFIG[key]
}

type Props = FollowUsOnSocialMediaData

export function FollowUsOnSocialMedia({
  title = 'Follow us on social media',
  links,
}: Props) {
  const styles = getStyles(defaultStyles)

  const socialLinks = links.filter((item) => Boolean(item.link))

  if (!socialLinks.length) return null

  return (
    <Box component="section" {...styles('wrapper')}>
      <Typography variant="body1.600" color="text.primary" textAlign="center">
        {title}
      </Typography>

      <Box {...styles('links')}>
        {socialLinks.map((item) => {
          const config = getPlatformConfig(item.label)
          const color = config?.color || 'primary.main'

          return (
            <Box
              key={`${item.label}-${item.link}`}
              component="a"
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={item.label}
              {...styles('link', {
                borderColor: color,
                color,
              })}
            >
              {item.imageData?.src && (
                <Image
                  src={item.imageData.src}
                  alt={item.imageData.alt || item.label}
                  width={16}
                  height={16}
                  style={{ objectFit: 'contain' }}
                  {...styles('icon')}
                />
              )}
              {!item.imageData?.src && config && (
                <Icon
                  iconName={config.iconName}
                  sx={{ color, fontSize: '16px' }}
                />
              )}
              <Typography variant="caption1.500" sx={{ color }}>
                {item.label}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
